import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getAssetUrl } from "../utils/assetUrl";
import {
  isCapturedGameUrl,
  toLobbyFrameSrc,
  unwrapGameLaunchUrl,
} from "../utils/gameFrameSrc";
import "./GameScreenEmbed.css";

type GameScreenEmbedProps = {
  url: string;
  name?: string;
  title?: string;
  loading?: boolean;
};

const PHONE_W = 390;
const PHONE_H = 844;
const MOBILE_MQ = "(max-width: 900px)";
const FRAME_ALLOW =
  "autoplay; fullscreen; encrypted-media; payment; clipboard-read; clipboard-write";

type Pane = "lobby" | "game";

function PhoneGameFrame({ homeUrl, title }: { homeUrl: string; title: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<HTMLIFrameElement>(null);
  const frameStateRef = useRef({ scale: 1, height: PHONE_H });
  const [pane, setPane] = useState<Pane>("lobby");
  const [gameReady, setGameReady] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_MQ).matches
  );
  const [frame, setFrame] = useState({ scale: 1, height: PHONE_H });

  useEffect(() => {
    setPane("lobby");
    setGameReady(false);
    const game = gameRef.current;
    if (game) game.src = "about:blank";
  }, [homeUrl]);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MQ);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const seenKeys = new Set<string>();
    const openGame = (raw: string, via: string) => {
      const href = unwrapGameLaunchUrl(raw);
      if (!isCapturedGameUrl(href)) {
        console.warn("[LINK-TAB skip]", via, href.slice(0, 80));
        return false;
      }
      try {
        const parsed = new URL(href, window.location.href);
        const host = parsed.hostname.toLowerCase();
        const isLobby =
          host === "localhost" ||
          host === "127.0.0.1" ||
          host.endsWith("gg8835.com");
        if (isLobby) {
          const relay =
            (parsed.pathname.includes("/pages/game/") &&
              parsed.searchParams.has("storageKey")) ||
            parsed.pathname.includes("/pages/open/");
          if (!relay) return false;
        }
      } catch {
        return false;
      }
      console.log("[LINK-TAB open]", via, href.replace(/\?.*/, "?…"));
      fetch("/__thn-hit", {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: `${via} ${href}`,
        keepalive: true,
      }).catch(() => {});
      const game = gameRef.current;
      if (game) game.src = href;
      setGameReady(true);
      setPane("game");
      return true;
    };

    const scanStorage = () => {
      try {
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i);
          if (
            !key ||
            seenKeys.has(key) ||
            (!key.startsWith("web_lobby_url_redirect_") &&
              !key.startsWith("web_lobby_htmlData_"))
          ) {
            continue;
          }
          const value = window.localStorage.getItem(key);
          if (!value || value.trim().startsWith("<")) continue;
          seenKeys.add(key);
          openGame(value, "localStorage");
        }
      } catch {
        // private mode
      }
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.source !== "thn-embed" || data.type !== "NAV") return;
      if (typeof data.url === "string") openGame(data.url, "postMessage");
    };

    const nativeOpen = window.open.bind(window);
    window.open = ((url?: string | URL, target?: string, features?: string) => {
      const href = url == null ? "" : String(url);
      if (href && openGame(href, "window.open")) {
        return gameRef.current?.contentWindow ?? window;
      }
      return nativeOpen(url as string, target, features);
    }) as typeof window.open;

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", scanStorage);
    const timer = window.setInterval(scanStorage, 400);
    return () => {
      window.open = nativeOpen;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", scanStorage);
      window.clearInterval(timer);
    };
  }, []);

  useLayoutEffect(() => {
    if (isMobile) return;
    const el = boxRef.current;
    if (!el) return;

    const fit = () => {
      const width = el.clientWidth || PHONE_W;
      const height = el.clientHeight || PHONE_H;
      const scale = width / PHONE_W;
      const next = {
        scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
        height: scale > 0 ? height / scale : PHONE_H,
      };
      const prev = frameStateRef.current;
      if (
        Math.abs(prev.scale - next.scale) < 0.01 &&
        Math.abs(prev.height - next.height) < 2
      ) {
        return;
      }
      frameStateRef.current = next;
      setFrame(next);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile]);

  const frameStyle = isMobile
    ? undefined
    : {
        width: PHONE_W,
        height: frame.height,
        transform: `scale(${frame.scale})`,
      };

  return (
    <div className={`game-screen-embed is-phone${isMobile ? " is-native" : ""}`}>
      <div className="game-screen-embed__tabs">
        <button
          type="button"
          className={pane === "lobby" ? "is-on" : ""}
          onClick={() => setPane("lobby")}
        >
          GG88
        </button>
        <button
          type="button"
          className={pane === "game" ? "is-on" : ""}
          disabled={!gameReady}
          onClick={() => setPane("game")}
        >
          LINK
        </button>
      </div>
      <div ref={boxRef} className="game-screen-embed__stage">
        <iframe
          className={`game-screen-embed__frame${pane === "lobby" ? " is-on" : ""}`}
          name="gg88-embed"
          src={toLobbyFrameSrc(homeUrl)}
          title={title}
          width={isMobile ? undefined : PHONE_W}
          height={isMobile ? undefined : Math.round(frame.height)}
          style={frameStyle}
          allow={FRAME_ALLOW}
          referrerPolicy="no-referrer-when-downgrade"
        />
        <iframe
          ref={gameRef}
          className={`game-screen-embed__frame${pane === "game" ? " is-on" : ""}`}
          name="gg88-game"
          title="Game link"
          width={isMobile ? undefined : PHONE_W}
          height={isMobile ? undefined : Math.round(frame.height)}
          style={frameStyle}
          allow={FRAME_ALLOW}
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
}

export function GameScreenEmbed({
  url,
  name = "GG88",
  title,
  loading = false,
}: GameScreenEmbedProps) {
  if (loading) {
    return (
      <div className="game-screen-embed is-loading">
        Đang tải màn hình game...
      </div>
    );
  }

  if (url) {
    return <PhoneGameFrame homeUrl={url} title={title || name || "Màn hình game"} />;
  }

  return (
    <div className="game-screen-embed is-empty">
      <div className="game-screen-embed__icon">
        <img src={getAssetUrl("/assets/thn/icon-pad.svg")} alt="" width={40} height={29} />
      </div>
      <p className="game-screen-embed__name">{name}</p>
      <p className="game-screen-embed__status">[ LINK.VERIFIED ]</p>
      <span className="game-screen-embed__cta is-disabled">
        <img src={getAssetUrl("/assets/thn/icon-play.svg")} alt="" width={9} height={12} />
        Truy cập ngay
      </span>
    </div>
  );
}

export default GameScreenEmbed;
